import React, { useState, useRef, useEffect } from "react";
import MapTo from "../utils/mapto";
import TabsIcon from "@images/icons/tabs-icon.svg";
import styled from 'styled-components';

const TabsContainer = styled.div`
    .tabs-header {
        position: relative;
        border-radius: 48px;
        isolation: isolate;
        box-shadow: 0px 10px 50px -10px #0000004D, 0px 0px 50px 0px #0000000D inset;
        backdrop-filter: blur(40px);

        &:before {
            content: '';
            position: absolute;
            inset: -1px;
            border-radius: 48px;
            padding: 1px;
            background: linear-gradient(163deg,rgba(255,255,255,1.7) 5%,rgba(92,143,137,0.2) 10%,rgba(92,143,137,0.2) 91%,rgba(255,255,255,0.70) 95%);
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            z-index: -1;
        }
    }

    .active-tab {
        position: relative;
        border-radius: 48px;
        isolation: isolate;
        box-shadow: 0px 20px 50px -20px #00000033, 0px 0px 50px 0px #0000000D inset;
        backdrop-filter: blur(40px);
        &:before {
            content: '';
            position: absolute;
            inset: -1px;
            border-radius: 48px;
            padding: 1px;
            background: linear-gradient(130deg, #FFF 7.86%, rgba(92, 143, 137, 1) 12.11%, rgba(92, 143, 137, 1) 88.58%, #FFF 92.83%);
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            z-index: -1;
        }
    }

    .mobile-tab-content {
        overflow: hidden;
        transition: max-height 0.5s ease-in-out, opacity 0.3s ease-in-out;
    }
`;

const Tabs = (props) => {
    const [activeTab, setActiveTab] = useState(props.items[0]?.id || "");
    const [hoveredTab, setHoveredTab] = useState(null);
    const [indicatorStyle, setIndicatorStyle] = useState({});
    const [isMobile, setIsMobile] = useState(false);
    const tabsRef = useRef({});
    const contentRefs = useRef({});
    const tabsContainerRef = useRef(null);

    const scrollToTop = () => {
        if (tabsContainerRef.current) {
            const headerHeight = 120; // Default header height
            const targetPosition = tabsContainerRef.current.getBoundingClientRect().top + window.pageYOffset - headerHeight;
            
            window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });
        }
    };

    const getContent = (items) => {
        if (items?.length > 0) {
            return items.map((item) => (
                <MapTo items={item} key={item.id} className="tabs-content-item" />
            ));
        }
        return null;
    };

    // Check if screen is mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);

    // Update indicator position when active tab changes or on hover
    useEffect(() => {
        const currentTab = hoveredTab || activeTab;
        const tabElement = tabsRef.current[currentTab];

        if (tabElement) {
            const tabsContainer = tabElement.parentElement;
            const containerLeft = tabsContainer.getBoundingClientRect().left;
            const tabLeft = tabElement.getBoundingClientRect().left;

            setIndicatorStyle({
                width: `${tabElement.offsetWidth}px`,
                transform: `translateX(${tabLeft - containerLeft}px)`,
                transition: 'transform 0.3s ease-out, width 0.3s ease-out'
            });
        }
    }, [activeTab, hoveredTab, isMobile]);

    // Set content height for smooth transitions
    useEffect(() => {
        if (!isMobile) return;

        // Reset all content heights
        Object.keys(contentRefs.current).forEach(id => {
            const element = contentRefs.current[id];
            if (element) {
                if (id === activeTab) {
                    element.style.maxHeight = 'none';
                    element.style.opacity = '1';
                } else {
                    element.style.maxHeight = '0';
                    element.style.opacity = '0';
                }
            }
        });
    }, [activeTab, isMobile]);

    const handleMouseEnter = (tabId) => {
        if (!isMobile) {
            setHoveredTab(tabId);
        }
    };

    const handleMouseLeave = () => {
        if (!isMobile) {
            setHoveredTab(null);
        }
    };

    const useSlidesOnMobile = props.items.length <= 2;

    return (
        <TabsContainer className="tabs-container w-full" ref={tabsContainerRef}>
            {/* Non-mobile view */}
            {(!isMobile || useSlidesOnMobile) && (
                <>
                    <div
                        className="tabs-header flex flex-nowrap bg-[#00000033] rounded-[32px] relative"
                        onMouseLeave={handleMouseLeave}
                    >
                        {/* Sliding indicator */}
                        <div
                            className="absolute bottom-0 h-full bg-gradient-references rounded-full z-0 transition-all duration-300"
                            style={indicatorStyle}
                        ></div>

                        {props.items.map((item) => (
                            <div
                                ref={el => tabsRef.current[item.id] = el}
                                key={item.id}
                                className={`flex items-center justify-center whitespace-normal z-10 tabs-header-item tabstarget cursor-pointer text-paragraph leading-[18px] py-[14px] px-[20px] text-center transition-colors duration-200 flex-1 min-w-[120px]
                                    ${activeTab === item.id
                                        ? "active-tab text-white font-medium"
                                        : "text-white hover:text-white"}`}
                                onClick={() => setActiveTab(item.id)}
                                onMouseEnter={() => handleMouseEnter(item.id)}
                            >
                                {item.label}
                            </div>
                        ))}
                    </div>
                    <div className="tabs-content mt-6">
                        {props.items.map((item) => (
                            <div
                                key={item.id}
                                className={`${activeTab === item.id ? "block" : "hidden"}`}
                            >
                                {getContent(item.items)}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Mobile accordion view */}
            {(isMobile && !useSlidesOnMobile) && (
                <div className="mobile-tabs flex flex-col">
                    {props.items.map((item, i, arr) => {
                        const isLast = i === arr.length - 1;
                        return (
                            <div key={item.id} className={`mobile-tab-item ${isLast ? '' : 'border-b border-soft_blue'}`}>
                                <div
                                    className={`mobile-tab-header py-[24px] px-0 cursor-pointer text-white font-medium transition-all duration-300 flex justify-between items-center ${activeTab === item.id ? "border-b border-soft_blue" : ""}`}
                                    onClick={() => {
                                        const newTabId = activeTab === item.id ? null : item.id;
                                        setActiveTab(newTabId);
                                        if (newTabId) {
                                            scrollToTop();
                                        }
                                    }}
                                >
                                    <span className={`${activeTab === item.id ? "font-bold" : ""} text-p_mobile`}>{item.label}</span>
                                    <img src={TabsIcon} alt="Tabs Icon" className={`w-[32px] h-[32px] transition-transform duration-300 ${activeTab === item.id ? "" : "rotate-180"}`} />
                                </div>
                                <div
                                    ref={el => contentRefs.current[item.id] = el}
                                    className="mobile-tab-content"
                                    style={{
                                        opacity: activeTab === item.id ? 1 : 0,
                                    }}
                                >
                                    <div className="py-6">
                                        {getContent(item.items)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </TabsContainer>
    );
};

export default Tabs;